/**
 * v2 API contract of «Выплаты» (/payouts): what was paid to people this month, carry-over, and the
 * employer taxes by employing entity and by project (docs/research/design2-month-ops.md §3, §5.5–5.8,
 * §6.3; decisions-log V13–V15, V22–V24).
 */
import { z } from 'zod';
import { isPeriod } from '../dates';
import { CURRENCIES } from '../money';
import { REMAINDERS } from './receipts';

/** Pay tables of the screen, in order (and the claim order of V24); `share` = % of a project (V69). */
export const PAYOUT_FORMATS = ['hourly', 'monthly', 'scope', 'share'] as const;
export type PayoutFormat = (typeof PAYOUT_FORMATS)[number];

/** `planned` shows «План», `approved` «Согласовано», `paid` «Выплачено» (V13). */
export type PayoutStatus = 'planned' | 'approved' | 'paid';

export const payoutPeriodSchema = z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ');

export const payoutsQuerySchema = z.object({
  month: payoutPeriodSchema,
  issuer: z.string().min(1).default('all'),
});
export type PayoutsQuery = z.infer<typeof payoutsQuerySchema>;

/** One person × currency in one pay table. */
export interface PayoutRowView {
  readonly personId: string;
  readonly name: string;
  readonly currency: string;
  readonly projectNames: readonly string[];
  /** hourly: Σ amount / Σ hours, rounded to a cent (design «средняя ставка»); null without hours. */
  readonly avgRateE4: number | null;
  /** hourly: Σ entered hours, the plan where none is entered. */
  readonly minutes: number;
  /** monthly: Σ monthly amounts; scope: Σ scope totals. */
  readonly payTotalMinor: number;
  /** The month's work in this table, without carry. */
  readonly amountMinor: number;
  /** True while an hourly line uses the plan or a scope line has no stage: «Итого» shows «—». */
  readonly preliminary: boolean;
  /** This table holds the carry and the «Выплачено» input of the person × currency (V24). */
  readonly claimed: boolean;
  /** Carry into the month (V14); 0 when not claimed. */
  readonly carryMinor: number;
  /** amount + carry. */
  readonly totalMinor: number;
  /** Live due of the person × currency over every format, carry included. */
  readonly dueMinor: number;
  /** The person has more than one format in this currency. */
  readonly multiFormat: boolean;
  readonly paidMinor: number | null;
  /** Paid in another currency (V57): that currency and the amount sent; null otherwise. */
  readonly paidCurrency: string | null;
  readonly paidOriginalMinor: number | null;
  readonly paidOn: string | null;
  /** Paid with «Подтвердить выплату» (V64): the difference to what was due does not carry. */
  /** Paid, and «План выплат» changed a line afterwards (V131): yellow, ↳ carry or ✓ confirm. */
  readonly changed: boolean;
  readonly settled: boolean;
  /** «Перенесено» (V167): what goes into a later month, typed; null while nothing carries or not paid. */
  readonly carriedMinor: number | null;
  /**
   * The month «Перенесено» goes into (V219, V284): where it went, or — while nothing carries — the
   * next month; null without a remainder.
   */
  readonly carryTo: string | null;
  /** Paid: due − paid, the most «Перенесено» can take; null until paid. */
  readonly remainderMinor: number | null;
  /** Σ of the parts' remainders accepted with ✓ (V299), the remainder's sign; 0 while nothing is. */
  readonly writtenOffMinor: number;
  /** Paid with a remainder: «Перенесено» can change (a paid target month asks first, V284). */
  readonly carryEditable: boolean;
  readonly status: PayoutStatus;
  /** Payout row for the Activity focus; null while the month has none. */
  readonly payoutId: string | null;
  /** The carry from the previous month and this month's advances (V211); null when there is none. */
  readonly carryIn: PayoutCarryIn | null;
}

/** A project of a person paid directly (V211): what it adds up to this month, the salary's part left out. */
export interface PayoutProjectLine {
  /** The part's key for «Выплачено» (V212). */
  readonly assignmentId: string;
  readonly projectId: string;
  readonly projectName: string;
  /** hourly: hours (entered, else the plan); null for the other formats. */
  readonly minutes: number | null;
  readonly amountMinor: number;
  /** Hours or a stage still to enter: the amount may change. */
  readonly preliminary: boolean;
  /** «Выплачено» for the project (V212); null = not recorded. */
  readonly paidMinor: number | null;
  /** This project's part of «Перенесено на …» (V279, V299), the sign of its remainder; null = nothing. */
  readonly carriedMinor: number | null;
  /** The project's remainder accepted with ✓ (V299): written off, not carried; null = nothing. */
  readonly writtenOffMinor: number | null;
}

/**
 * «Перенесено с {месяц}» (V167, V211): what the previous month's payment carried into this one, typed
 * there and editable here while this month is not paid; advances paid this month are taken off.
 */
export interface PayoutCarryIn {
  /** The months whose «Перенесено» goes into this one (V219), oldest first; empty with advances only. */
  readonly periods: readonly string[];
  /** Σ of what they carried. */
  readonly carriedMinor: number;
  /** Advances paid during this month, taken off what is due. */
  readonly advancesMinor: number;
  /** «Выплачено» of the carry (V212); null = not recorded. */
  readonly paidMinor: number | null;
  /** The carry's own part of «Перенесено на …» (V299); null = nothing of it carries on. */
  readonly carriedOutMinor: number | null;
  /** The carry's remainder accepted with ✓ (V299), written off; null = nothing. */
  readonly writtenOffMinor: number | null;
}

/**
 * A person × currency in the table of the entity that pays them (V208): the totals of every pay type,
 * the type lines, «По проектам» with the project payout's «Выплачено» / «Перенесено» / ✓, and the
 * salary through this entity with its advance and salary inputs.
 */
export interface PayoutPersonGroup {
  readonly personId: string;
  readonly name: string;
  readonly currency: string;
  /** Σ due of everything: the project payout (work + carry) and the salary on hand. */
  readonly dueMinor: number;
  /** Σ paid of everything; null while nothing is paid. */
  readonly paidMinor: number | null;
  /** The projects paid directly, one line each (V211). */
  readonly projectLines: readonly PayoutProjectLine[];
  /** The project payout of the person × currency; null without project pay or carry here. */
  readonly projects: PayoutRowView | null;
  /** The salary through this entity in this currency; null without one. */
  readonly salary: PayoutSalaryRow | null;
}

/** «Выплаты» per paying entity (V208): the person's entity for project pay, the salary's entity for a salary. */
export interface PayoutTable {
  /** null: people not employed in any of our entities («Без юрлица»). */
  readonly entityId: string | null;
  readonly entityName: string;
  readonly people: readonly PayoutPersonGroup[];
}

/** Derived state of a tax line: «К уплате», «Просрочено», «Уплачено», «Уплачено частично», «Уплачено n из m». */
export type TaxState = 'due' | 'late' | 'paid' | 'partial' | 'some';

export interface TaxEmployee {
  readonly personId: string;
  readonly name: string;
  readonly incomeMinor: number;
  readonly pensionMinor: number;
  readonly employerMinor: number;
  readonly totalMinor: number;
  readonly paidMinor: number | null;
  readonly state: TaxState;
}

/** Employer taxes of one employing entity × currency. */
export interface TaxEntityGroup {
  readonly entityId: string;
  readonly entityName: string;
  readonly currency: string;
  /** The 15th of the next month. */
  readonly dueDate: string;
  /** Rates in basis points for the «Пенсионный» tooltip. */
  readonly pensionBp: number;
  readonly employerBp: number;
  readonly incomeMinor: number;
  /** Employee pension + employer contribution. */
  readonly pensionMinor: number;
  readonly totalMinor: number;
  /** Σ paid, null while nobody is paid. */
  readonly paidMinor: number | null;
  readonly paidCount: number;
  readonly state: TaxState;
  readonly employees: readonly TaxEmployee[];
}

/** Income tax of the month allocated to a project by the employees' line amounts. */
export interface TaxProjectRow {
  readonly projectId: string;
  readonly name: string;
  /** The entity that pays the tax on the project's revenue (V220): the month's issuer of the project; null without one. */
  readonly entityId: string | null;
  /** Its name, «Без юрлица» without one. */
  readonly entityName: string;
  readonly currency: string;
  readonly dueDate: string;
  /** To pay: the entity's taxes on the project's revenue (V160), = `revenueTaxMinor`. */
  readonly incomeMinor: number;
  /** The entity's taxes on the project's revenue of the month (V160, V298): turnover plus income; 0 when none. */
  readonly revenueTaxMinor: number;
  /** «Налог с оборота» of the revenue at `turnoverTaxRateE6` (the project's override, else the entity's). */
  readonly turnoverTaxMinor: number;
  readonly turnoverTaxRateE6: number;
  /** The entity's own «Подоходный с выручки» at `entityIncomeTaxRateE6` (V298); 0 without the rate. */
  readonly entityIncomeTaxMinor: number;
  readonly entityIncomeTaxRateE6: number;
  /** What was recorded for the project; null when nothing is. */
  readonly paidMinor: number | null;
  /** Derived from the employees' payments when the project has no record of its own. */
  readonly derivedPaidMinor: number | null;
  readonly employeeNames: readonly string[];
  readonly state: TaxState;
}

/**
 * A salary through one of our entities on «На руки» (V69, V71, V258): what the month's approved and
 * planned entries give on hand and what was paid of it so far — the advance and the rest go into
 * the same amount, so «К выплате» is what is left.
 */
export interface PayoutSalaryRow {
  readonly personId: string;
  readonly name: string;
  readonly entityId: string;
  readonly entityName: string;
  readonly currency: string;
  /** Σ every entry (advance, salary, vacation, sick, bonus, compensation) on hand. */
  readonly dueMinor: number;
  /** The advance part of `dueMinor` (V71); 0 without an advance. Shown in the tooltip only. */
  readonly advanceMinor: number;
  /** «Выплачено» so far, the advance included; null = nothing recorded. */
  readonly paidMinor: number | null;
  /** Closed with ✓ (V296): paid less or more than due, the difference written off — the row is done. */
  readonly settled: boolean;
  /** Every entry is approved. */
  readonly approved: boolean;
}

export interface PayoutsResponse {
  readonly period: string;
  /** One per paying entity, by name, «Без юрлица» last (V208). */
  readonly tables: readonly PayoutTable[];
  readonly taxEntities: readonly TaxEntityGroup[];
  readonly taxProjects: readonly TaxProjectRow[];
}

/** POST /payouts/approve — approves the person × currency payout of a month (V23). */
export const payoutApproveSchema = z.object({
  personId: z.string().min(1),
  currency: z.string().min(1),
  period: payoutPeriodSchema,
});
export type PayoutApprove = z.infer<typeof payoutApproveSchema>;


/** Amount in minor units; null clears it. */
export const paidAmountSchema = z.object({
  amountMinor: z.number().int().min(0, 'Сумма — число от 0').nullable(),
});
export type PaidAmount = z.infer<typeof paidAmountSchema>;

/**
 * PUT /salary/:entityId/:currency/:period/:personId — the salary paid on hand so far (V71, V258);
 * `settle` (V296) closes it: the difference to the amount due is written off and the row is done.
 */
export const salaryPaidSchema = paidAmountSchema.extend({
  remainder: z.enum(REMAINDERS).default('carry'),
});
export type SalaryPaid = z.infer<typeof salaryPaidSchema>;
/** The request body (`remainder` defaults to `carry`). */
export type SalaryPaidBody = z.input<typeof salaryPaidSchema>;

/**
 * «Выплачено» of a payout (V57): the amount, optionally in another currency than the payout's;
 * the server converts it at the day's rate for the carry. `settle` (V64) pays the month off: what
 * differs from the due amount does not carry to the next month.
 */
export const payoutPaidSchema = paidAmountSchema.extend({
  currency: z.enum(CURRENCIES).optional(),
  remainder: z.enum(REMAINDERS).default('carry'),
});
export type PayoutPaid = z.infer<typeof payoutPaidSchema>;
/** The request body (`remainder` defaults to `carry`). */
export type PayoutPaidBody = z.input<typeof payoutPaidSchema>;

/**
 * PUT /payouts/:personId/:currency/:period/parts/:part/paid — «Выплачено» of one project (`part` = its
 * assignment) or of «Перенесено с …» (`part` = `carry`), in the payout's currency (V212); null clears it.
 */
export const payoutPartPaidSchema = z.object({ amountMinor: z.number().int().min(0, 'Сумма — число от 0').nullable() }).strict();
export type PayoutPartPaid = z.infer<typeof payoutPartPaidSchema>;
export const CARRY_PART = 'carry';

/**
 * PUT /payouts/:personId/:currency/:period/carried — «Перенесено» (V167); null or 0 carries nothing.
 * It goes into the next month; `into` names a later month instead (V284, the first unpaid one). A
 * month already paid takes it only with `confirm` — its payout is then marked changed (V131).
 */
export const payoutCarriedSchema = z
  .object({
    amountMinor: z.number().int().nullable(),
    into: payoutPeriodSchema.optional(),
    confirm: z.boolean().optional(),
  })
  .strict();
export type PayoutCarried = z.infer<typeof payoutCarriedSchema>;

/**
 * PUT /payouts/:personId/:currency/:period/parts/:part/carried — ↳ of one part (V299): what of the
 * part's remainder goes into the next month (the same body as the payout's «Перенесено»); the
 * payout's «Перенесено» is the parts' sum.
 */
export const payoutPartCarriedSchema = payoutCarriedSchema;
export type PayoutPartCarried = PayoutCarried;

/**
 * PUT /payouts/:personId/:currency/:period/parts/:part/settled — ✓ of one part (V299): `true` accepts
 * what is left of the part's remainder (it is written off, nothing carries), `false` opens it again.
 */
export const payoutPartSettledSchema = z.object({ settled: z.boolean() }).strict();
export type PayoutPartSettled = z.infer<typeof payoutPartSettledSchema>;

export interface PayoutWriteResult {
  readonly message: string;
  readonly undoToken: string | null;
}
